import { useCallback } from 'react';
import clsx from 'clsx';
import { Checkbox } from '@headlessui/react';

import { GroupedOption, SelectOption, Account } from '~/shared/utils/types';
import Typography from '~/shared/components/Typography';
import CheckIcon from '~/shared/assets/icons/check.svg';

import {
  buildFilterSearchAriaLabel,
  volumeFilterSearchInputId,
} from '~/shared/components/searchInputMeta';

import { useSearchFilter } from './common/useSearchFilter';
import SearchFilterBase from './common/SearchFilterBase';

export default function SearchAccount({
  filters,
  setFilters,
  accounts = [],
  isDisclosureOpen,
}: {
  filters: SelectOption[];
  setFilters: (selections: SelectOption[]) => void;
  accounts?: Account[];
  isDisclosureOpen?: boolean;
}) {
  const searchOptions = useCallback(
    async (pattern: string): Promise<GroupedOption[]> => {
      const trimmedPattern = pattern.trim().toLowerCase();

      if (!trimmedPattern) {
        // Return all accounts when no search pattern
        return accounts.length > 0
          ? [
              {
                label: 'Accounts',
                options: accounts.map((account) => ({
                  id: account.accountId,
                  name: account.accountName,
                  group: 'accountId' as const,
                })),
              },
            ]
          : [];
      }

      try {
        // Filter accounts client-side based on account name
        const filteredAccounts = accounts.filter((account) =>
          account.accountName.toLowerCase().includes(trimmedPattern)
        );

        const groupedResults: GroupedOption[] =
          filteredAccounts.length > 0
            ? [
                {
                  label: 'Accounts',
                  options: filteredAccounts.map((account) => ({
                    id: account.accountId,
                    name: account.accountName,
                    group: 'accountId' as const,
                  })),
                },
              ]
            : [];

        return groupedResults;
      } catch (error) {
        console.error('[AccountFilter] Error filtering accounts:', error);
        return [];
      }
    },
    [accounts]
  );

  const {
    searchResults,
    isLoading,
    inputValue,
    showResults,
    focusedIndex,
    localSelections,
    selectedIdSet,
    flattenedResults,
    inputRef,
    resultsRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleSelect,
    handleResultsMouseDown,
    handleResultsMouseUp,
    handleKeyDown,
  } = useSearchFilter({
    initialSelections: filters,
    onSelectionsChange: setFilters,
    searchFunction: searchOptions,
    filterGroup: 'accountId',
    debounceMs: 200,
    defaultQuery: '', // No default query for accounts
    isDisclosureOpen,
  });

  const renderOption = useCallback(
    (option: SelectOption, isSelected: boolean, isFocused: boolean = false) => (
      <div
        key={option.id}
        className={clsx(
          'p-3 my-1 rounded-lg transition-colors flex items-center',
          isFocused && 'bg-primary-20'
        )}
        onClick={() => handleSelect(option)}
      >
        <div className="mr-2">
          <Checkbox
            checked={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              handleSelect(option);
            }}
            className={clsx(
              'group block size-5 p-[4px] rounded-md border border-gray-400 bg-white data-checked:bg-primary-500 ring:0 active:ring-primary-300 focus:ring-primary-500 cursor-pointer',
              isFocused && 'border-primary-400'
            )}
          >
            <CheckIcon className="text-white stroke-2 size-2.5 opacity-0 group-data-checked:opacity-100 transition duration-200 ease-in-out" />
          </Checkbox>
        </div>
        <Typography
          variant="p3-reg"
          className={clsx('text-primary-900 cursor-pointer', isSelected ? 'font-500' : 'font-400')}
        >
          {option.name}
        </Typography>
      </div>
    ),
    [handleSelect]
  );

  return (
    <div className="flex w-full flex-1 flex-col">
      <SearchFilterBase
        inputValue={inputValue}
        onInputChange={handleInputChange}
        inputRef={inputRef}
        isDisclosureOpen={isDisclosureOpen}
        searchInputId={volumeFilterSearchInputId('account')}
        searchAriaLabel={buildFilterSearchAriaLabel('Accounts')}
        placeholder="Search accounts..."
        searchResults={searchResults}
        isLoading={isLoading}
        showResults={showResults}
        focusedIndex={focusedIndex}
        flattenedResults={flattenedResults}
        localSelections={localSelections}
        onSelect={handleSelect}
        selectedIdSet={selectedIdSet}
        renderOption={renderOption}
        className="w-full"
        mustTypeFirstMessage="Start typing to search accounts"
        resultsRef={resultsRef}
        onResultsMouseDown={handleResultsMouseDown}
        onResultsMouseUp={handleResultsMouseUp}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
      />
    </div>
  );
}
